import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../../ui/button';
import LoadingRing from '../../ui-components/LoadingRing';
import MachineDetailView from '../../room-overview/MachineDetailView';
import { MRIMachinePopulated } from '../../room-overview/types';
import { MachineCompatibilityDTO } from '../../../services/api/mriMachineCompatibilityService';
import CautionBanner from './CautionBanner';

interface CompatibilityMachineDetailProps {
    loading: boolean;
    machine: MRIMachinePopulated | null;
    compatibilityResult: MachineCompatibilityDTO | undefined;
    implantCoilWarning: string | null;
    onBack: () => void;
}

export default function CompatibilityMachineDetail({
    loading,
    machine,
    compatibilityResult,
    implantCoilWarning,
    onBack,
}: CompatibilityMachineDetailProps) {
    const { t } = useTranslation();

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (!machine) return null;

    const hasGradientCaution = compatibilityResult?.isRestricted && !!compatibilityResult.gradientAnalysis;

    return (
        <div className="space-y-4">
            <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="gap-1 -ml-2"
            >
                <ArrowLeft className="h-4 w-4" />
                {t('mriCompatibility.detail.backToResults')}
            </Button>
            {(hasGradientCaution || implantCoilWarning) && (
                <div className="sticky -top-6 z-10 space-y-4 bg-background pt-6 pb-4">
                    {hasGradientCaution && (
                        <CautionBanner
                            icon={AlertTriangle}
                            title={t('mriCompatibility.detail.spatialGradientCondition')}
                            description={t('mriCompatibility.detail.positioningRestrictionAction')}
                            tooltip={t('mriCompatibility.detail.gradientExplanation')}
                            referenceBlock={{
                                label: t('mriCompatibility.implantConditionLabel'),
                                content: t('mriCompatibility.detail.implantMaxGradient', {
                                    value: compatibilityResult!.gradientAnalysis!.implantMaxGradient,
                                    gauss: Math.round(compatibilityResult!.gradientAnalysis!.implantMaxGradient * 100),
                                }),
                            }}
                        />
                    )}
                    {implantCoilWarning && (
                        <CautionBanner
                            icon={Info}
                            title={t('mriCompatibility.coilInteraction')}
                            description={t('mriCompatibility.coilInteractionAction')}
                            referenceBlock={{
                                label: t('mriCompatibility.implantConditionLabel'),
                                content: implantCoilWarning,
                            }}
                        />
                    )}
                </div>
            )}
            <MachineDetailView
                machine={machine}
                readOnly
                compatibility={{
                    gradientAnalysis: compatibilityResult?.gradientAnalysis ?? null,
                    isRestricted: compatibilityResult?.isRestricted,
                    coilWarning: implantCoilWarning,
                }}
            />
        </div>
    );
}
